import React, { useMemo, useState } from "react";
import {
  Calendar,
  Bell,
  MapPin,
  Zap,
  Clock,
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Cake,
  Users,
  CircleAlert,
  CircleCheck,
  X,
  Repeat,
  LinkIcon,
  AlertCircle,
  CheckCircle2,
  Tag,
  FileText,
  Star,
  ChevronDown,
  List,
} from "lucide-react";
import HeaderMenu from "../../Navbar/headermenu";
import CreateEventDrawer from "./CreateEventDrawer";
import CustomFooter from "../../Footer/customFooter";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";

/* ─── static data ─── */
const EVENTS = [
  {
    id: 1,
    type: "birthday",
    title: "Lucifer MorningStar",
    dateLabel: "MONDAY – 01 JUN",
    time: "All Day",
    fullDate: "Jun 1, 2026",
  },
  {
    id: 2,
    type: "event",
    title: "Meeting with Kuldip",
    dateLabel: "FRIDAY – 19 JUN",
    time: "16:07 – 17:07",
    fullDate: "Jun 19, 2026",
    invitees: ["LM"],
    client: "Select client",
    tags: [],
    url: "www.simplifyte.com",
    createdBy: "Lucifer MorningStar",
    createdAt: "19-Jun-2026 at 10:37 AM",
  },
];

const REMINDERS = [
  {
    id: 1,
    title: "Q1 GST Filing Deadline",
    dateLabel: "OVERDUE",
    time: "09:00 AM",
    fullDate: "Apr 10, 2026",
    status: "overdue",
    tags: ["GST", "Urgent"],
  },
  {
    id: 2,
    title: "TDS Return – May",
    dateLabel: "OVERDUE",
    time: "05:00 PM",
    fullDate: "May 31, 2026",
    status: "overdue",
    tags: ["TDS"],
  },
  {
    id: 3,
    title: "test",
    dateLabel: "TODAY – FRIDAY, 19 JUNE 2026",
    time: "07:00 PM",
    fullDate: "Jun 19, 2026",
    status: "pending",
    tags: [],
    invitees: ["LM"],
  },
];

/* mini calendar data for June 2026 */
// June 1 2026 = Tuesday  → offset 2
const CAL_OFFSET = 2;
const CAL_DAYS = 30;
const EVENT_DOTS = {
  1: ["birthday"],
  19: ["event", "reminder"],
  20: ["reminder"],
  9: ["reminder"],
};

/* ─── sub-components ─── */

function CalMini({ activeTab }) {
  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < CAL_OFFSET; i++) arr.push(null);
    for (let d = 1; d <= CAL_DAYS; d++) arr.push(d);
    return arr;
  }, []);

  const dotColor = (type) => {
    if (type === "birthday") return "#ec4899";
    if (type === "event") return "#3b82f6";
    return "#10b981";
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-2 sm:p-4 select-none w-full">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <span className="font-bold text-gray-900 text-sm sm:text-base">
          June <span className="text-gray-500 font-normal">2026</span>
          <ChevronRight className="inline w-3 h-3 sm:w-4 sm:h-4 text-gray-400 ml-1" />
        </span>
        <div className="flex gap-0.5 sm:gap-1">
          <button className="p-0.5 sm:p-1 rounded hover:bg-gray-100">
            <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
          </button>
          <button className="px-1.5 sm:px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-600">
            Today
          </button>
          <button className="p-0.5 sm:p-1 rounded hover:bg-gray-100">
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-0 text-center mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className={`text-xs font-semibold pb-1 ${i === 0 ? "text-red-500" : "text-gray-500"}`}>
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0 text-center">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const isToday = d === 19;
          const dots = EVENT_DOTS[d] || [];
          return (
            <div key={d} className="flex flex-col items-center py-0.5">
              <div
                className={`w-5 h-5 sm:w-7 sm:h-7 flex items-center justify-center rounded-full text-xs sm:text-sm
                  ${isToday ? "bg-[#093a5b] text-white font-bold" : "text-gray-700 hover:bg-gray-100 cursor-pointer"}`}
              >
                {d}
              </div>
              {dots.length > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {dots.slice(0, 3).map((type, idx) => (
                    <div
                      key={idx}
                      style={{ background: dotColor(type) }}
                      className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full"
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventDetailPanel({ event, onClose }) {
  if (!event) return null;
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 30 }}
      transition={{ duration: 0.22 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col"
    >
      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-gray-400">
          <ChevronRight className="w-4 h-4" />
          <span className="text-xs">…</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100 text-gray-400"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 flex-1 overflow-y-auto space-y-4">
        {/* type badge + update */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <div>
              <p className="text-sm font-semibold text-blue-900">Event</p>
              <p className="text-xs text-blue-500">Set your team's next milestone or meeting in seconds.</p>
            </div>
          </div>
          <button className="text-xs border border-gray-300 rounded px-3 py-1.5 text-gray-600 hover:bg-gray-50">
            Update
          </button>
        </div>

        {/* title */}
        <div>
          <h3 className="text-lg font-bold text-gray-900">{event.title}</h3>
          <p className="text-sm text-gray-400 mt-0.5">Enter Subtitle</p>
        </div>

        {/* date/time */}
        <div className="bg-gray-50 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-20">All Day Event</span>
            <div className="w-8 h-4 bg-gray-300 rounded-full relative cursor-pointer">
              <div className="absolute left-0.5 top-0.5 w-3 h-3 bg-white rounded-full shadow" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-20">Starts</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">19 Jun 2026</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">04:07 PM</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-20">Ends</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">19 Jun 2026</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">05:07 PM</span>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <div className="text-right">
              <p className="text-2xl font-bold text-[#093a5b]">19</p>
              <p className="text-xs text-blue-600 font-medium">Friday</p>
              <p className="text-xs text-gray-500">June</p>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg px-3 py-2">
              <Calendar className="w-3.5 h-3.5 text-blue-500" />
              Today's Date
            </div>
          </div>
        </div>

        {/* fields */}
        {[
          { label: "Invitees", value: <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Lucifer MorningStar (You)</span> },
          { label: "Add Client", value: <span className="text-gray-400 text-sm">Select client or client group</span> },
          { label: "Remarks", value: <span className="text-gray-400 text-sm">Remarks Content</span> },
          { label: "Tags", value: <span className="text-gray-400 text-sm flex items-center gap-1">Select Tags <ChevronDown className="w-3.5 h-3.5" /></span> },
          { label: "URL", value: <span className="text-gray-400 text-sm">www.simplifyte.com</span> },
          { label: "Created By", value: (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#093a5b] text-white text-xs flex items-center justify-center font-semibold">LM</div>
              <div>
                <p className="text-sm text-gray-800 font-medium">Lucifer MorningStar</p>
                <p className="text-xs text-gray-500">19-Jun-2026 at 10:37 AM</p>
              </div>
            </div>
          )},
          { label: "Last Updated", value: <span className="text-gray-400 text-sm italic">Not updated yet</span> },
        ].map(({ label, value }) => (
          <div key={label} className="flex items-start gap-4 py-1 border-b border-gray-50">
            <span className="text-xs text-gray-500 w-24 pt-0.5 shrink-0">{label}</span>
            <div className="flex-1">{value}</div>
          </div>
        ))}

        {/* Notes */}
        <div className="bg-[#093a5b] rounded-xl px-4 py-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-semibold">Notes</span>
        </div>
        <p className="text-gray-400 text-sm italic px-1">Enter text or type '/' for commands</p>
      </div>
    </motion.div>
  );
}

function ReminderDetailPanel({ reminder, onClose }) {
  if (!reminder) return null;
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 30 }}
      transition={{ duration: 0.22 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-gray-400">
          <ChevronRight className="w-4 h-4" />
          <span className="text-xs">…</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-orange-50 border border-orange-200 text-orange-600 px-2 py-1 rounded-full font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" /> Pending
          </span>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-5 flex-1 overflow-y-auto space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-lg px-3 py-2">
            <Bell className="w-4 h-4 text-purple-600" />
            <div>
              <p className="text-sm font-semibold text-purple-900">Reminder</p>
              <p className="text-xs text-purple-500">Set a reminder in seconds</p>
            </div>
          </div>
          <button className="text-xs border border-gray-300 rounded px-3 py-1.5 text-gray-600 hover:bg-gray-50">
            Update
          </button>
        </div>

        <div>
          <h3 className="text-lg font-bold text-gray-900">{reminder.title}</h3>
          <p className="text-sm text-gray-400 mt-0.5">Enter Subtitle</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-28">All Day Reminder</span>
            <div className="w-8 h-4 bg-gray-300 rounded-full relative cursor-pointer">
              <div className="absolute left-0.5 top-0.5 w-3 h-3 bg-white rounded-full shadow" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-28">Date</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">19-Jun-2026</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-28">Time</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">07:00 PM</span>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <div className="text-right">
              <p className="text-2xl font-bold text-[#093a5b]">19</p>
              <p className="text-xs text-blue-600 font-medium">Friday</p>
              <p className="text-xs text-gray-500">June</p>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg px-3 py-2">
              <Calendar className="w-3.5 h-3.5 text-blue-500" />
              Today's Date
            </div>
          </div>
        </div>

        {[
          { label: "Invitees", value: <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Lucifer MorningStar (You)</span> },
          { label: "Remarks", value: <span className="text-gray-400 text-sm">Remarks Content</span> },
          { label: "Tags", value: <span className="text-gray-400 text-sm flex items-center gap-1">Select Tags <ChevronDown className="w-3.5 h-3.5" /></span> },
          { label: "URL", value: <span className="text-gray-400 text-sm">www.simplifyte.com</span> },
          { label: "Created By", value: (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#093a5b] text-white text-xs flex items-center justify-center font-semibold">LM</div>
              <div>
                <p className="text-sm text-gray-800 font-medium">Lucifer MorningStar</p>
                <p className="text-xs text-gray-500">19-Jun-2026 02:49 PM</p>
              </div>
            </div>
          )},
        ].map(({ label, value }) => (
          <div key={label} className="flex items-start gap-4 py-1 border-b border-gray-50">
            <span className="text-xs text-gray-500 w-24 pt-0.5 shrink-0">{label}</span>
            <div className="flex-1">{value}</div>
          </div>
        ))}

        <div className="bg-[#093a5b] rounded-xl px-4 py-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-semibold">Notes</span>
        </div>
        <p className="text-gray-400 text-sm italic px-1">Enter text or type '/' for commands</p>
      </div>
    </motion.div>
  );
}

/* ─── split-panel demo ─── */
function EventsSplitDemo() {
  const [selected, setSelected] = useState(EVENTS[1]);

  const byDate = [
    { dateLabel: "MONDAY – 01 JUN", items: [EVENTS[0]] },
    { dateLabel: "FRIDAY – 19 JUN", items: [EVENTS[1]] },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
      {/* top bar */}
      <div className="flex items-center justify-between px-3 sm:px-5 py-3 border-b border-gray-100 gap-2">
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <span className="text-base sm:text-xl font-bold text-[#093a5b]">Events</span>
          <span className="text-gray-300">|</span>
          <span className="text-base sm:text-xl font-semibold text-gray-400">Reminders</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="text-center">
            <p className="text-[#ec4899] font-bold text-sm sm:text-base leading-none">1</p>
            <p className="text-xs text-gray-400 hidden sm:block">BirthDays</p>
          </div>
          <div className="text-center">
            <p className="text-[#3b82f6] font-bold text-sm sm:text-base leading-none">1</p>
            <p className="text-xs text-gray-400 hidden sm:block">Events</p>
          </div>
          <div className="text-center">
            <p className="text-[#093a5b] font-black text-lg sm:text-2xl leading-none">19</p>
            <p className="text-xs text-blue-600 font-medium hidden sm:block">Friday</p>
            <p className="text-xs text-gray-400 hidden sm:block">June</p>
          </div>
        </div>
      </div>

      {/* tabs */}
      <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-5 py-2 border-b border-gray-100 overflow-x-auto">
        <button className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-semibold text-[#093a5b] border-b-2 border-[#093a5b] pb-1.5 shrink-0">
          <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Home
        </button>
        <button className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-400 pb-1.5 shrink-0">
          <List className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> List
        </button>
        <div className="hidden sm:flex items-center gap-2 ml-2 text-xs text-gray-400">
          <span className="flex items-center gap-1 border border-gray-200 rounded px-2 py-0.5 text-pink-500">
            <Cake className="w-3 h-3" /> Birthday
          </span>
          <span className="flex items-center gap-1 border border-gray-200 rounded px-2 py-0.5 text-blue-500">
            <Calendar className="w-3 h-3" /> Events
          </span>
          <span className="text-gray-400">+ Filter</span>
        </div>
      </div>

      {/* body */}
      <div className="flex min-h-[280px] sm:min-h-[340px]">
        {/* left — list (full width on mobile, 3/5 on sm+) */}
        <div className="flex-1 sm:w-3/5 border-r border-gray-100 overflow-y-auto">
          {byDate.map((group) => (
            <div key={group.dateLabel}>
              <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-bold text-gray-400 tracking-wide">{group.dateLabel}</span>
                <span className="text-gray-400 text-base sm:text-lg leading-none">–</span>
              </div>
              {group.items.map((ev) => (
                <div
                  key={ev.id}
                  onClick={() => setSelected(ev)}
                  className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 cursor-pointer border-b border-gray-50 hover:bg-blue-50 transition-colors ${selected?.id === ev.id ? "bg-blue-50" : ""}`}
                >
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md flex items-center justify-center shrink-0 ${ev.type === "birthday" ? "text-pink-500" : "text-blue-500"}`}>
                    {ev.type === "birthday" ? <Cake className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-gray-800 font-medium truncate">{ev.title}</p>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">{ev.time}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* right — mini cal (hidden on mobile) */}
        <div className="hidden sm:block sm:w-2/5 p-2 sm:p-3">
          <CalMini />
        </div>
      </div>
    </div>
  );
}

function EventDetailDemo() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg w-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <ChevronRight className="w-4 h-4" /> <span>…</span>
        </div>
      </div>
      <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 flex-1 mr-3">
            <Calendar className="w-4 h-4 text-blue-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-900">Event</p>
              <p className="text-xs text-blue-500">Set your team's next milestone in seconds.</p>
            </div>
          </div>
          <button className="text-xs border border-gray-300 rounded px-3 py-1.5 text-gray-600 shrink-0">Update</button>
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">Meeting with Kuldip</h3>
          <p className="text-xs text-gray-400 mt-0.5">Enter Subtitle</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16">Starts</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">19 Jun 2026</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">04:07 PM</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16">Ends</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">19 Jun 2026</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">05:07 PM</span>
          </div>
        </div>
        {[
          { label: "Invitees", value: <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">Lucifer (You)</span> },
          { label: "Client", value: <span className="text-gray-400 text-xs">Select client</span> },
          { label: "Tags", value: <span className="text-gray-400 text-xs">Select Tags</span> },
          { label: "URL", value: <span className="text-gray-400 text-xs truncate">www.simplifyte.com</span> },
        ].map(({ label, value }) => (
          <div key={label} className="flex items-center gap-2 py-1.5 border-b border-gray-50">
            <span className="text-xs text-gray-400 w-14 sm:w-20 shrink-0">{label}</span>
            <div className="flex-1 min-w-0">{value}</div>
          </div>
        ))}
        <div className="bg-[#093a5b] rounded-xl px-4 py-2.5 flex items-center gap-2">
          <FileText className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-semibold">Notes</span>
        </div>
      </div>
    </div>
  );
}

function RemindersSplitDemo() {
  const overdue = REMINDERS.filter((r) => r.status === "overdue");
  const today = REMINDERS.filter((r) => r.status === "pending");

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
      {/* top bar */}
      <div className="flex items-center justify-between px-3 sm:px-5 py-3 border-b border-gray-100 gap-2">
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <span className="text-base sm:text-xl font-semibold text-gray-400">Events</span>
          <span className="text-gray-300">|</span>
          <span className="text-base sm:text-xl font-bold text-[#093a5b]">Reminders</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="text-center">
            <p className="text-orange-500 font-bold text-sm sm:text-base leading-none">1</p>
            <p className="text-xs text-gray-400 hidden sm:block">Due Today</p>
          </div>
          <div className="text-center">
            <p className="text-red-500 font-bold text-sm sm:text-base leading-none">8</p>
            <p className="text-xs text-gray-400 hidden sm:block">Overdue</p>
          </div>
          <div className="hidden sm:block text-center">
            <p className="text-green-500 font-bold text-base leading-none">3</p>
            <p className="text-xs text-gray-400">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-[#093a5b] font-black text-lg sm:text-2xl leading-none">19</p>
            <p className="text-xs text-blue-600 font-medium hidden sm:block">Friday</p>
            <p className="text-xs text-gray-400 hidden sm:block">June</p>
          </div>
        </div>
      </div>

      {/* tabs */}
      <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-5 py-2 border-b border-gray-100 overflow-x-auto">
        <button className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-semibold text-[#093a5b] border-b-2 border-[#093a5b] pb-1.5 shrink-0">
          <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Home
        </button>
        <button className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-400 pb-1.5 shrink-0">
          <List className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> List
        </button>
        <span className="hidden sm:block text-xs text-gray-400 ml-2 shrink-0">☰ Task Reminder</span>
        <span className="hidden sm:block text-xs text-gray-400 shrink-0">+ Filter</span>
      </div>

      {/* body */}
      <div className="flex min-h-[280px] sm:min-h-[340px]">
        {/* left (full width on mobile) */}
        <div className="flex-1 sm:w-3/5 border-r border-gray-100 overflow-y-auto">
          {/* overdue banner */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-red-50 border-b border-red-100">
            <span className="text-xs font-bold text-red-600 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> OVERDUE 8
            </span>
            <span className="text-xs text-red-400 flex items-center gap-1">
              Show <ChevronDown className="w-3 h-3" />
            </span>
          </div>
          {/* today section */}
          <div className="px-3 sm:px-4 py-2 bg-white border-b border-gray-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-gray-500">Today – 19 Jun 2026</span>
              <span className="bg-[#093a5b] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" /> PENDING 1
              </span>
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <Check className="w-2.5 h-2.5" /> DONE 1
              </span>
            </div>
          </div>
          {today.map((r) => (
            <div key={r.id} className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 border-b border-gray-50 hover:bg-purple-50 cursor-pointer transition-colors">
              <Bell className="w-4 h-4 text-purple-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs sm:text-sm text-gray-800 font-medium truncate block">{r.title}</span>
              </div>
              <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full border border-orange-200 shrink-0">pending</span>
              <span className="text-xs text-gray-400 shrink-0 hidden sm:block">{r.time}</span>
            </div>
          ))}
        </div>

        {/* right — mini cal (hidden on mobile) */}
        <div className="hidden sm:block sm:w-2/5 p-2 sm:p-3">
          <CalMini />
        </div>
      </div>
    </div>
  );
}

function ReminderDetailDemo() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg w-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <ChevronRight className="w-4 h-4" /> <span>…</span>
        </div>
        <span className="text-xs bg-orange-50 border border-orange-200 text-orange-600 px-2 py-1 rounded-full font-medium flex items-center gap-1">
          <Clock className="w-3 h-3" /> Pending
        </span>
      </div>
      <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-lg px-3 py-2 flex-1 mr-3">
            <Bell className="w-4 h-4 text-purple-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-purple-900">Reminder</p>
              <p className="text-xs text-purple-500">Set a reminder in seconds</p>
            </div>
          </div>
          <button className="text-xs border border-gray-300 rounded px-3 py-1.5 text-gray-600 shrink-0">Update</button>
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">test</h3>
          <p className="text-xs text-gray-400 mt-0.5">Enter Subtitle</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-20">Date</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">19-Jun-2026</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-20">Time</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">07:00 PM</span>
          </div>
        </div>
        {[
          { label: "Invitees", value: <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">Lucifer (You)</span> },
          { label: "Tags", value: <span className="text-gray-400 text-xs">Select Tags</span> },
          { label: "URL", value: <span className="text-gray-400 text-xs truncate">www.simplifyte.com</span> },
          { label: "Created By", value: (
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-[#093a5b] text-white text-xs flex items-center justify-center font-semibold shrink-0">LM</div>
              <span className="text-xs text-gray-700 truncate">Lucifer MorningStar</span>
            </div>
          )},
        ].map(({ label, value }) => (
          <div key={label} className="flex items-center gap-2 py-1.5 border-b border-gray-50">
            <span className="text-xs text-gray-400 w-14 sm:w-20 shrink-0">{label}</span>
            <div className="flex-1 min-w-0">{value}</div>
          </div>
        ))}
        <div className="bg-[#093a5b] rounded-xl px-4 py-2.5 flex items-center gap-2">
          <FileText className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-semibold">Notes</span>
        </div>
        <p className="text-gray-400 text-xs italic px-1">Enter text or type '/' for commands</p>
      </div>
    </div>
  );
}

/* ─── feature section wrapper ─── */
function FeatureSection({ badge, badgeColor, title, description, bullets, bulletColor, demo, reverse = false }) {
  return (
    <motion.div
      className="mb-24 md:mb-36"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
    >
      <div className={`grid md:grid-cols-2 gap-8 lg:gap-16 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
        <div>
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4"
            style={{ background: badgeColor + "18", color: badgeColor }}
          >
            {badge}
          </span>
          <h2
            className="font-bold text-gray-900 mb-4 tracking-tight"
            style={{ fontSize: "clamp(1.5rem,3.5vw,2.25rem)", lineHeight: 1.2 }}
          >
            {title}
          </h2>
          <p className="text-gray-500 mb-6 leading-relaxed" style={{ fontSize: "clamp(0.875rem,1.5vw,1rem)" }}>
            {description}
          </p>
          <ul className="space-y-4">
            {bullets.map((b, i) => (
              <li key={i} className="flex gap-3">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: bulletColor + "20" }}
                >
                  <Check className="w-3 h-3" style={{ color: bulletColor }} />
                </div>
                <div>
                  <p className="font-semibold text-gray-800" style={{ fontSize: "clamp(0.8rem,1.3vw,0.95rem)" }}>
                    {b.title}
                  </p>
                  <p className="text-gray-500" style={{ fontSize: "clamp(0.78rem,1.2vw,0.9rem)" }}>
                    {b.desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="w-full min-w-0"
        >
          {demo}
        </motion.div>
      </div>
    </motion.div>
  );
}

/* ─── main component ─── */
export default function SmartSchedulingCalendar() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleCreateEvent = (newEvent) => {
    setIsCreateOpen(false);
    setSelectedDate(null);
  };

  return (
    <>
      <Helmet>
        <title>Smart Scheduling | Simplifyte</title>
      </Helmet>
      <HeaderMenu />

      {/* ── HERO ── */}
      <div className="relative bg-white min-h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-blue-500/5 rounded-full blur-[120px]" />
        </div>
        <section className="docs_Tabs w-full">
          <div className="relative z-10 max-w-7xl mx-auto section-padding-responsive_SmartCal text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h1 className="hero-main-title text-[#093a5b] tracking-tight">
                Everything.
                <br />
                One place.
              </h1>
              <p className="hero-main-desc text-[#94A3B8] max-w-3xl mx-auto">
                Events. Reminders. Birthdays.
                <br />
                All in one intelligent calendar.
              </p>
            </motion.div>

            {/* hero demo — two product cards side by side */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid md:grid-cols-2 max-w-5xl mx-auto gap-6 mt-12 mb-8 text-left"
            >
              {/* Events hero card */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-bold text-gray-900">Events</span>
                  <div className="ml-auto flex gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                </div>
                <div className="p-4 space-y-2.5">
                  {[
                    { icon: <Cake className="w-4 h-4 text-pink-500" />, title: "Lucifer MorningStar", sub: "All Day · Jun 1", color: "bg-pink-50 border-pink-100" },
                    { icon: <Calendar className="w-4 h-4 text-blue-500" />, title: "Meeting with Kuldip", sub: "16:07–17:07 · Jun 19", color: "bg-blue-50 border-blue-100", active: true },
                  ].map((e, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${e.color} ${e.active ? "ring-1 ring-blue-300" : ""}`}>
                      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-gray-100 shadow-sm">
                        {e.icon}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{e.title}</p>
                        <p className="text-xs text-gray-500">{e.sub}</p>
                      </div>
                      {e.active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Reminders hero card */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center shrink-0">
                    <Bell className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-bold text-gray-900">Reminders</span>
                  <div className="ml-auto flex items-center gap-1.5 sm:gap-3 text-xs">
                    <span className="text-red-500 font-semibold whitespace-nowrap">8 Over</span>
                    <span className="text-orange-500 font-semibold whitespace-nowrap hidden sm:inline">1 Due</span>
                    <span className="text-green-500 font-semibold whitespace-nowrap">3 Done</span>
                  </div>
                </div>
                <div className="p-4 space-y-2.5">
                  <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                    <span className="text-xs font-bold text-red-600">OVERDUE 8</span>
                    <span className="ml-auto text-xs text-red-400 flex items-center gap-0.5">Show <ChevronDown className="w-3 h-3" /></span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-orange-100 bg-orange-50">
                    <Bell className="w-4 h-4 text-purple-500 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">test</p>
                      <p className="text-xs text-gray-500">Today, 07:00 PM</p>
                    </div>
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full border border-orange-200">pending</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-green-100 bg-green-50">
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-500 line-through">GST Filing Q1</p>
                      <p className="text-xs text-gray-400">Jun 9, 2026</p>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">done</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <p className="Hero_SubText text-[#94A3B8]">
              Two powerful systems. One seamless experience.
            </p>
          </div>
        </section>
      </div>

      {/* ── FEATURE SECTIONS ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">

        {/* Feature 1 — Events split view */}
        <FeatureSection
          badge="Smart Events"
          badgeColor="#3b82f6"
          title="Your team's calendar, beautifully organized."
          description="See every event at a glance — birthdays, meetings, milestones. A split view keeps your list and mini calendar always in sync."
          bullets={[
            { title: "Date-grouped list view", desc: "Events sorted by date with clear section headers — Monday, Friday, next week." },
            { title: "Mini calendar sync", desc: "Color-coded dots show birthdays (pink), events (blue), and reminders (green) at a glance." },
            { title: "One-click detail panel", desc: "Click any event to open a rich detail panel with invitees, client, tags, notes, and more." },
          ]}
          bulletColor="#3b82f6"
          demo={<EventsSplitDemo />}
        />

        {/* Feature 2 — Event detail */}
        <FeatureSection
          badge="Rich Event Details"
          badgeColor="#0284c7"
          title="Everything about an event, in one place."
          description="Click any event to open a detailed panel — set start/end times, add invitees, link a client, attach tags and URLs, and write rich notes."
          bullets={[
            { title: "Invitees & clients", desc: "Tag team members and link client accounts directly to the event." },
            { title: "Flexible time setting", desc: "All-day toggle or precise start/end times with date picker." },
            { title: "Notes with commands", desc: "Rich text notes with '/' commands — add checklists, headings, and more." },
          ]}
          bulletColor="#0284c7"
          demo={<EventDetailDemo />}
          reverse
        />

        {/* Feature 3 — Reminders split view */}
        <FeatureSection
          badge="Smart Reminders"
          badgeColor="#7c3aed"
          title="Never let a deadline slip through."
          description="Reminders show you what's overdue, pending today, and completed — with live counts in the header so you always know where you stand."
          bullets={[
            { title: "Overdue banner", desc: "A red banner surfaces all overdue reminders — dismiss them individually or clear all." },
            { title: "Pending / Completed toggle", desc: "Filter today's reminders between pending and completed with a single click." },
            { title: "Live status counters", desc: "Due Today, Overdue, and Completed counts update in real time at the top of the page." },
          ]}
          bulletColor="#7c3aed"
          demo={<RemindersSplitDemo />}
        />

        {/* Feature 4 — Reminder detail */}
        <FeatureSection
          badge="Reminder Details"
          badgeColor="#9333ea"
          title="Set, edit, and track reminders in seconds."
          description="Open any reminder to see full details — date, time, invitees, tags, and rich notes. Mark as pending, complete, or snooze right from the panel."
          bullets={[
            { title: "Pending status badge", desc: "Clearly shows if the reminder is pending, completed, or overdue at the top." },
            { title: "Invitees & tags", desc: "Assign reminders to team members and categorize with tags for easy filtering." },
            { title: "Notes section", desc: "Add rich context notes to every reminder so nothing falls through the cracks." },
          ]}
          bulletColor="#9333ea"
          demo={<ReminderDetailDemo />}
          reverse
        />
      </section>

      {/* ── CREATE IN SECONDS ── */}
      <section className="section-padding-responsive" style={{ background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20-responsive">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6 mx-auto"
              style={{ background: "linear-gradient(135deg, #093a5b, #1d6fa4)" }}>
              <Zap className="w-7 h-7 text-white" />
            </div>
            <h2 className="hero-title-smart-class text-gray-900 mb-4 tracking-tight">
              Create in seconds.
              <br />
              Not minutes.
            </h2>
            <p className="hero-subtitle text-gray-500 max-w-xl mx-auto">
              Watch the magic happen
            </p>
          </div>

          <div className="grid md:grid-cols-2 hero-stack max-w-5xl mx-auto gap-6">
            {/* Old way */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                  <X className="w-3.5 h-3.5 text-red-500" />
                </div>
                <h3 className="section-title text-gray-700 !mb-0">The old way</h3>
              </div>
              <div className="bg-white rounded-2xl card-box border border-gray-200 shadow-sm space-y-0 divide-y divide-gray-50">
                {[
                  "Open calendar app",
                  "Navigate to correct date",
                  "Click create event",
                  "Fill multiple forms",
                  "Add location separately",
                  "Invite people one by one",
                  "Set reminder in different app",
                  "Hope you didn't forget anything",
                ].map((text, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                    <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-400 text-xs flex items-center justify-center font-semibold shrink-0">
                      {idx + 1}
                    </span>
                    <span className="smart-scheduling-text text-gray-500">{text}</span>
                  </div>
                ))}
                <div className="pt-3 mt-2 border-t border-red-100 !border-t">
                  <div className="flex items-center gap-2 bg-red-50 rounded-xl px-3 py-2">
                    <Clock className="w-4 h-4 text-red-400 shrink-0" />
                    <p className="text-red-500 text-sm font-medium">~2 minutes average</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Simplifyte way */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <h3 className="section-title text-gray-900 !mb-0">The Simplifyte way</h3>
              </div>
              <div className="rounded-2xl card-box space-y-4 overflow-hidden relative"
                style={{ background: "linear-gradient(135deg, #1e40af 0%, #0284c7 50%, #06b6d4 100%)" }}>
                {/* glow effect */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                {[
                  { step: "01", text: "Click any date" },
                  { step: "02", text: "Type what you need" },
                  { step: "03", text: "Done." },
                ].map(({ step, text }, idx) => (
                  <div key={idx} className="flex items-center gap-4 relative z-10">
                    <div className="w-8 h-8 rounded-full bg-white/20 border border-white/30 flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-white/50 text-xs font-mono">{step}</span>
                      <p className="text-white font-semibold feature-text leading-tight">{text}</p>
                    </div>
                  </div>
                ))}
                <div className="zap-row border-t border-white/20 relative z-10">
                  <div className="flex items-center gap-2 bg-white/15 rounded-xl px-4 py-3 border border-white/20">
                    <Zap className="zap-icon text-yellow-300" />
                    <p className="text-white font-bold zap-text">10 seconds. Really.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BIRTHDAY SECTION ── */}
      <section className="relative overflow-hidden section-padding-responsive bg-white">
        {/* soft blue glow behind — theme colors */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full blur-3xl opacity-20"
            style={{ background: "radial-gradient(ellipse, #bfdbfe 0%, #93c5fd 40%, transparent 70%)" }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* two-column: text left, demo right */}
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* LEFT — text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6"
                style={{ background: "linear-gradient(135deg, #093a5b, #1d6fa4)" }}
              >
                <Cake className="w-7 h-7 text-white" />
              </motion.div>

              <h2 className="font-bold text-gray-900 mb-4 tracking-tight"
                style={{ fontSize: "clamp(1.75rem,4vw,2.75rem)", lineHeight: 1.15 }}>
                Never forget
                <br />
                <span style={{ WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  a birthday again.
                </span>
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed" style={{ fontSize: "clamp(0.9rem,1.5vw,1.05rem)" }}>
                Birthdays from your contacts are automatically added to your calendar. You get a reminder days before — so you always show up for the people who matter.
              </p>

              <ul className="space-y-4">
                {[
                  { icon: <Cake className="w-4 h-4" />, color: "#093a5b", text: "Auto-imported from your contacts list" },
                  { icon: <Bell className="w-4 h-4" />, color: "#1d6fa4", text: "Reminder set 3 days before, automatically" },
                  { icon: <CircleCheck className="w-4 h-4" />, color: "#10b981", text: "All Day event — no time entry needed" },
                ].map((b, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: b.color + "18", color: b.color }}>
                      {b.icon}
                    </div>
                    <span className="text-gray-700 font-medium" style={{ fontSize: "clamp(0.85rem,1.3vw,0.95rem)" }}>
                      {b.text}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* RIGHT — product-style demo */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="w-full min-w-0"
            >
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">

                {/* app top bar — matches actual product header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-[#093a5b]">Events</span>
                    <span className="text-gray-300 font-light">|</span>
                    <span className="text-lg font-semibold text-gray-400">Reminders</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <p className="text-[#ec4899] font-bold text-sm leading-none">2</p>
                      <p className="text-xs text-gray-400">BirthDays</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[#3b82f6] font-bold text-sm leading-none">1</p>
                      <p className="text-xs text-gray-400">Events</p>
                    </div>
                    <div className="text-center border-l border-gray-200 pl-3">
                      <p className="text-[#093a5b] font-black text-xl leading-none">19</p>
                      <p className="text-xs text-blue-600 font-medium">Friday</p>
                      <p className="text-xs text-gray-400">June</p>
                    </div>
                  </div>
                </div>

                {/* tabs — Home | List + Birthday filter active */}
                <div className="flex items-center gap-4 px-5 py-2 border-b border-gray-100">
                  <button className="flex items-center gap-1.5 text-sm font-semibold text-[#093a5b] border-b-2 border-[#093a5b] pb-1.5">
                    <Calendar className="w-3.5 h-3.5" /> Home
                  </button>
                  <button className="flex items-center gap-1.5 text-sm text-gray-400 pb-1.5">
                    <List className="w-3.5 h-3.5" /> List
                  </button>
                  <div className="flex items-center gap-2 ml-2 text-xs">
                    <span className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-md px-2 py-0.5 text-[#093a5b] font-medium">
                      <Cake className="w-3 h-3" /> Birthday
                    </span>
                    <span className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-0.5 text-gray-400">
                      <Calendar className="w-3 h-3" /> Events
                    </span>
                    <span className="text-gray-400">+ Filter</span>
                  </div>
                </div>

                {/* birthday list — matches actual product exactly */}
                <div className="divide-y divide-gray-50">

                  {/* date header 1 */}
                  <div className="flex items-center justify-between px-5 py-2 bg-gray-50/80">
                    <span className="text-xs font-bold text-gray-400 tracking-widest">TUESDAY – 02 JUN</span>
                    <span className="text-gray-300 text-base leading-none">–</span>
                  </div>

                  {/* Lucifer MorningStar — from actual product */}
                  <div className="flex items-center gap-3 px-5 py-3.5 hover:bg-blue-50/50 transition-colors cursor-pointer">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(135deg,#093a5b,#1d6fa4)" }}>
                      <Cake className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Lucifer MorningStar</p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">All Day</span>
                  </div>

                  {/* date header 2 */}
                  <div className="flex items-center justify-between px-5 py-2 bg-gray-50/80">
                    <span className="text-xs font-bold text-gray-400 tracking-widest">THURSDAY – 18 DEC</span>
                    <span className="text-gray-300 text-base leading-none">–</span>
                  </div>

                  {/* Sarah's Birthday */}
                  <div className="flex items-center gap-3 px-5 py-3.5 hover:bg-blue-50/50 transition-colors cursor-pointer">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(135deg,#1d6fa4,#0ea5e9)" }}>
                      <Cake className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Sarah's Birthday</p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">All Day</span>
                  </div>

                  {/* date header 3 */}
                  <div className="flex items-center justify-between px-5 py-2 bg-gray-50/80">
                    <span className="text-xs font-bold text-gray-400 tracking-widest">WEDNESDAY – 25 DEC</span>
                    <span className="text-gray-300 text-base leading-none">–</span>
                  </div>

                  {/* Mom's Birthday */}
                  <div className="flex items-center gap-3 px-5 py-3.5 hover:bg-blue-50/50 transition-colors cursor-pointer">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(135deg,#0369a1,#093a5b)" }}>
                      <Cake className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Mom's Birthday</p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">All Day</span>
                  </div>

                </div>

                {/* bottom — reminder auto-set banner */}
                <div className="px-5 py-3 border-t border-gray-100 bg-gradient-to-r from-blue-50 to-sky-50">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <p className="text-xs text-gray-600 font-medium">
                      Reminders auto-set 3 days before each birthday
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── DESIGNED TO BE EFFORTLESS ── */}
      <section className="Section_Spacing" style={{ background: "linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-responsive-20">
            <h2 className="hero-title-white text-gray-900 mb-4 tracking-tight">
              Designed to be
              <br />
              effortless.
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto" style={{ fontSize: "clamp(0.875rem,1.5vw,1rem)" }}>
              Three pillars that make Simplifyte's calendar unlike any other.
            </p>
          </div>

          <div className="grid md:grid-cols-3 DocsGrid_Gap max-w-5xl mx-auto">
            {[
              {
                icon: <Calendar className="w-7 h-7 text-white" />,
                gradient: "linear-gradient(135deg, #3b82f6, #0ea5e9)",
                glow: "rgba(59,130,246,0.2)",
                title: "Events & Reminders",
                desc: "Two systems that work together seamlessly. Never choose between them again.",
                stat: "2-in-1",
                statLabel: "unified system",
              },
              {
                icon: <CheckCircle2 className="w-7 h-7 text-white" />,
                gradient: "linear-gradient(135deg, #7c3aed, #a855f7)",
                glow: "rgba(124,58,237,0.2)",
                title: "Smart Status Tracking",
                desc: "See what's overdue, pending, or complete at a glance. Stay in control.",
                stat: "3",
                statLabel: "status views",
              },
              {
                icon: <Cake className="w-7 h-7 text-white" />,
                gradient: "linear-gradient(135deg, #ec4899, #f43f5e)",
                glow: "rgba(236,72,153,0.2)",
                title: "Birthday Magic",
                desc: "Automatically tracked from contacts. Thoughtfully reminded. Never miss the moments that matter.",
                stat: "0",
                statLabel: "birthdays missed",
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-shadow text-center relative overflow-hidden"
              >
                {/* top glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-20 rounded-full blur-2xl pointer-events-none"
                  style={{ background: card.glow }} />
                <div className="relative z-10">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                    className="Calendar_Icon_Box rounded-2xl flex items-center justify-center mx-auto mb-5"
                    style={{ background: card.gradient }}
                  >
                    {card.icon}
                  </motion.div>
                  <h3 className="text-gray-900 font-bold mb-2 Events_Title">{card.title}</h3>
                  <p className="text-gray-500 leading-relaxed Events_Desc mb-5">{card.desc}</p>
                  <div className="inline-flex items-baseline gap-1 bg-gray-50 border border-gray-100 rounded-xl px-4 py-2">
                    <span className="text-2xl font-black" style={{ background: card.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                      {card.stat}
                    </span>
                    <span className="text-xs text-gray-400 font-medium">{card.statLabel}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL ── */}
      <section className="Testimonial_Section relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)" }}>
        {/* background blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-600/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* stars */}
          <div className="flex items-center justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <blockquote className="Testimonial_Quote text-white mb-8">
            What took minutes now takes seconds. I never miss anything –
            because everything lives in one place. The way a calendar should
            feel.
          </blockquote>
          <div className="flex items-center justify-center gap-3 md:gap-4">
            <div className="Testimonial_Avatar flex items-center justify-center text-white font-bold shrink-0"
              style={{ background: "linear-gradient(135deg, #3b82f6, #8b5cf6)" }}>
              ZA
            </div>
            <div className="text-left">
              <p className="text-white font-semibold Testimonial_Name">Mohhamad Zaki Asil</p>
              <p className="text-white/50 text-sm Testimonial_Role">Senior Developer, Frontend</p>
            </div>
          </div>
        </div>
      </section>

      <CreateEventDrawer
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        date={selectedDate}
        onCreate={handleCreateEvent}
      />

      <CustomFooter />
    </>
  );
}
